/*
 * The org's members, and the permission matrix each one currently has authored.
 *
 * READS COME FROM CLERK, WRITES GO THROUGH fleet-control. That split is the
 * design, not a shortcut: fleet-control stores the matrix in the member's Clerk
 * org-membership public metadata (`vgr`) via the Backend API, which makes it
 * browser-READABLE and browser-UNWRITABLE. Clerk then projects `vgr` into the
 * signed session JWT, so enforcement is in-token and needs no extra round trip.
 * Rendering the editor from the same field the server will enforce means the
 * screen cannot disagree with the token about what is currently granted.
 *
 * Clerk lives in the boot module; this file takes the pieces it needs as
 * arguments so a view never imports Clerk. Same reasoning as `app.aiToken`.
 */
/*
 * From the DOM-FREE subpath, not the barrel.
 *
 *  registers custom elements on import, so anything pulling the
 * barrel needs a DOM — which made this module, the one place a mistake silently
 * restores a revoked permission, impossible to cover with a plain node test. The
 * authorization vocabulary is not a UI concern (it lives in @vitesse/trust and is
 * re-exported here), so it gets a subpath that carries no elements with it.
 */
import { normalizeRole } from './identity';
/**
 * Parse the authored matrix out of a membership's public metadata.
 *
 * Tolerance is PER ENTRY, matching the Go verifier and the browser's identity
 * reader: one malformed entry must not discard the entries beside it, because
 * discarding them presents the editor as though an admin had granted things they
 * revoked — and the next Save would write that back.
 */
export function authoredGrants(meta) {
    const raw = meta?.['vgr'];
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    const out = {};
    for (const [key, value] of Object.entries(raw)) {
        if (typeof value === 'boolean')
            out[key] = value;
    }
    return out;
}
/**
 * Parse the ROLE ASSIGNMENT out of a membership's public metadata (`vrole`).
 *
 * Compact on the wire — `{r: name, o: {key: surfaces}}` — because it shares an
 * 8KB Clerk metadata budget with everything else the org stores there.
 *
 * Same per-entry tolerance as the matrix, for the same reason: one malformed
 * override must not discard the ones beside it, because the editor would then
 * show the role's value where a deliberate revocation used to be, and Save would
 * write that back.
 */
export function authoredRole(meta) {
    const raw = meta?.['vrole'];
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
        return { role: '', overrides: {} };
    const rec = raw;
    const role = typeof rec['r'] === 'string' ? rec['r'] : '';
    const overrides = {};
    const o = rec['o'];
    if (o != null && typeof o === 'object' && !Array.isArray(o)) {
        for (const [key, value] of Object.entries(o)) {
            if (typeof value === 'boolean')
                overrides[key] = value;
        }
    }
    return { role, overrides };
}
/**
 * Resolve a member to the matrix that actually ships — the role's grants with the
 * member's overrides on top. Mirrors authz.Flatten, and must keep mirroring it.
 *
 * An override is authoritative for its permission INCLUDING an override to the
 * empty list, which is the deliberate revocation. Falling back to the role there
 * would show an admin the access they had just taken away.
 *
 * This is a PREVIEW: the server flattens authoritatively on write. It exists so
 * the editor can show the consequence of an assignment before it is saved, which
 * is the whole reason to pick a role rather than 59 dropdowns.
 */
export function flatten(role, overrides) {
    const out = {};
    for (const [key, granted] of Object.entries(role?.grants ?? {}))
        out[key] = granted;
    for (const [key, granted] of Object.entries(overrides))
        out[key] = granted;
    return out;
}
/** Map one Clerk membership to the editor's model. */
export function toMember(m) {
    const d = m.publicUserData ?? {};
    const assignment = authoredRole(m.publicMetadata);
    const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.identifier || d.userId || '';
    // Only when it is not ALREADY the display name — repeating the same string on
    // two lines of one row reads as a rendering bug.
    const email = d.identifier && d.identifier !== name ? d.identifier : '';
    return {
        userId: d.userId ?? '',
        name,
        email,
        imageUrl: d.hasImage ? d.imageUrl ?? '' : '',
        joinedAtMs: m.createdAt ? new Date(m.createdAt).getTime() : 0,
        role: normalizeRole(m.role ?? ''),
        grants: authoredGrants(m.publicMetadata),
        assignedRole: assignment.role,
        overrides: assignment.overrides,
    };
}
/** The dropdown value for a row, given the member's authored matrix. */
export function choiceFor(grants, key) {
    const entry = grants[key];
    if (entry === undefined)
        return 'default';
    return entry ? 'granted' : 'revoked';
}
/** The authored value for a choice, or undefined to mean "leave the key absent". */
export function grantFor(choice) {
    switch (choice) {
        case 'default':
            return undefined; // absent = the role decides
        case 'granted':
            return true;
        default:
            return false; // authored false = revoked
    }
}
//# sourceMappingURL=org-members.js.map